import { storagePrefix } from '@/config';

/**
 * After a deploy, a page loaded before it asks for code chunks that no longer exist. Reloading once fetches the new
 * index.html and its chunks. The reload is remembered in sessionStorage, so a chunk that keeps failing shows the error
 * instead of reloading forever.
 *
 * A chunk also fails to load when there is no network, and a reload then leaves a home-screen app on the browser's
 * offline page, with no app and no way back. So the app never reloads while offline: it first confirms the server
 * answers (GET / with no cache), and otherwise waits for the next 'online' event while the error stays on screen.
 */

const storageKey = `${storagePrefix}chunk-reload`;

/** A second chunk failure within this time of an automatic reload shows the error. */
export const chunkReloadGuardMs = 30_000;

/** How long the check that the server answers may take; longer counts as no network. */
export const chunkReloadCheckTimeoutMs = 10_000;

const chunkErrorPatterns = [
  /failed to fetch dynamically imported module/i, // Chrome, Edge
  /error loading dynamically imported module/i, // Firefox
  /importing a module script failed/i, // Safari
  /is not a valid javascript mime type/i, // A server answering a missing chunk with index.html
  /unable to preload css/i, // Vite's CSS preload
  /loading (css )?chunk \S+ failed/i,
];

/** Whether an error means a code chunk could not be loaded (a lazy route's import, a preload). */
export function isChunkLoadError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;

  const { name, message } = error as { name?: unknown; message?: unknown };
  if (name === 'ChunkLoadError') return true;

  return typeof message === 'string' && chunkErrorPatterns.some((pattern) => pattern.test(message));
}

export type ChunkReloadState =
  /** No reload was asked for. */
  | 'idle'
  /** Confirming that the server answers before reloading: error screens show "Updating". */
  | 'checking'
  /** No network (offline, or the server did not answer): reloads on the next 'online' event. The error shows meanwhile. */
  | 'waitingForNetwork'
  /** location.reload() was called. */
  | 'reloading'
  /** The 30-second guard, or storage that does not work, refused the reload: the error shows. */
  | 'refused';

let state: ChunkReloadState = 'idle';
/** The running check, shared by every caller while it runs. */
let check: Promise<boolean> | null = null;
/** Bumped by the test reset, so a check from an earlier test cannot act on a later one. */
let generation = 0;
let stopWaiting: (() => void) | null = null;
const listeners = new Set<() => void>();

function setState(next: ChunkReloadState): void {
  if (state === next) return;
  state = next;
  for (const listener of [...listeners]) listener();
}

/** Where the automatic reload is (for error screens: "Updating", or the error with its Reload button). */
export function getChunkReloadState(): ChunkReloadState {
  return state;
}

/** Calls `listener` when the state changes. Returns the stop function. */
export function subscribeChunkReload(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** True while an automatic reload is checking or has started: error screens show "Updating" instead of the error. */
export function isChunkReloadPending(): boolean {
  return state === 'checking' || state === 'reloading';
}

/**
 * Whether the 30-second guard allows an automatic reload now: none in the last 30 seconds, and storage works to remember
 * this one (without it a broken chunk could reload forever). Connectivity is checked by `reloadForChunkError`.
 */
export function canReloadForChunkError(now: number = Date.now()): boolean {
  try {
    const stored = window.sessionStorage.getItem(storageKey);
    if (stored === null) return true;

    const last = Number(stored);
    // A time in the future means the clock was changed: allow it, the new time is stored.
    return !Number.isFinite(last) || now - last >= chunkReloadGuardMs || now < last;
  } catch {
    return false;
  }
}

function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/** Whether the server answers GET / (the app's index.html), bypassing every cache. A reload would load that page. */
async function serverAnswers(): Promise<boolean> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), chunkReloadCheckTimeoutMs);

  try {
    const response = await fetch('/', { cache: 'no-store', signal: controller.signal });
    return response.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

function refuse(): false {
  stopWaiting?.();
  setState('refused');
  return false;
}

/** Remembers that a reload is wanted and asks again on the next 'online' event. */
function waitForNetwork(): void {
  setState('waitingForNetwork');
  if (stopWaiting) return;

  const target = window;
  const onOnline = () => {
    stopWaiting?.();
    void reloadForChunkError();
  };
  target.addEventListener('online', onOnline);
  stopWaiting = () => {
    target.removeEventListener('online', onOnline);
    stopWaiting = null;
  };
}

function reloadNow(): boolean {
  // The check took time: the guard again, at the moment of the reload.
  const now = Date.now();
  if (!canReloadForChunkError(now)) return refuse();

  try {
    window.sessionStorage.setItem(storageKey, String(now));
  } catch {
    return refuse();
  }

  stopWaiting?.();
  setState('reloading');
  window.location.reload();
  return true;
}

/**
 * Asks for an automatic reload; used by the `vite:preloadError` handler and by the route error screens. It never
 * reloads while offline or when the server does not answer: it then waits for the next 'online' event (and checks again).
 * Within 30 seconds of the last automatic reload it refuses. Callers while a check runs share it.
 * Resolves true once the reload started, false when it was refused or is waiting for the network.
 */
export function reloadForChunkError(): Promise<boolean> {
  if (state === 'reloading') return Promise.resolve(true);
  if (check) return check;
  if (!canReloadForChunkError()) return Promise.resolve(refuse());

  if (isOffline()) {
    waitForNetwork();
    return Promise.resolve(false);
  }

  setState('checking');
  const started = generation;
  const running = (async () => {
    const answers = await serverAnswers();
    if (started !== generation) return false;
    check = null;

    if (!answers) {
      waitForNetwork();
      return false;
    }

    return reloadNow();
  })();
  check = running;
  return running;
}

/**
 * Listens for Vite's `vite:preloadError`, fired when a dynamic import or its preloads fail, and asks for a reload. The
 * event is never cancelled: whether to reload is known only once the server answered, so the import still fails and
 * the route's error screen shows "Updating" during the check, or the error when there is no network (or within 30
 * seconds of the last reload). Returns the stop function.
 */
export function installChunkReload(): () => void {
  const onPreloadError = () => {
    void reloadForChunkError();
  };

  window.addEventListener('vite:preloadError', onPreloadError);
  return () => window.removeEventListener('vite:preloadError', onPreloadError);
}

/** For tests. */
export function resetChunkReloadForTests(): void {
  generation += 1;
  stopWaiting?.();
  check = null;
  state = 'idle';
}
